import { Link as UILink } from '@askable/ui/core/link';
import add from 'date-fns/add';
import isAfter from 'date-fns/isAfter';
import { AlertTriangle } from 'lucide-react';
import { useMemo } from 'react';
import { Link } from 'src/lib/routing';
import { useBannerClass } from 'src/hooks/useBannerClass';
import { useQuery } from 'urql';

import { PaymentOverdueBanner } from 'components/PaymentOverdueBanner/data/PaymentOverdueBanner.query';
import { globalVariables } from 'lib/globalVariables';

type Transaction = {
  _id: string;
  status?: number | null;
  created?: number | null;
  invoice?: { amount_due?: number | null } | null;
};

export function paymentIsOverDue(transaction: Transaction | null | undefined): boolean {
  if (transaction?.status !== 2 || (transaction?.invoice?.amount_due ?? 0) <= 0) {
    return false;
  }

  return isAfter(new Date(), add(new Date(transaction?.created!), { days: 90 }));
}

type Props = {
  teamId: string;
};

export const PaymentOverDueBanner = ({ teamId }: Props) => {
  const [{ data, fetching }] = useQuery({
    query: PaymentOverdueBanner,
    variables: {
      teamId,
    },
  });

  const hasOverDuePayment = useMemo(() => {
    if (!data || fetching) {
      return false;
    }

    return data.transactionsByTeam?.some(paymentIsOverDue);
  }, [data, fetching]);

  const isDemo = globalVariables.getEnvironmentVariables().isDemo;
  const showBanner = !!hasOverDuePayment && !isDemo;

  useBannerClass(showBanner);

  if (!showBanner) {
    return null;
  }

  return (
    <div className="flex h-[46px] shrink-0 items-center justify-center bg-destructive p-2 text-center text-xs text-destructive-foreground sm:text-sm">
      <div>
        <AlertTriangle className="inline size-4" /> <strong>Your invoice is overdue.</strong> Please make a payment or
        your account may be frozen.{' '}
        <UILink asChild className="text-white">
          <Link to="/settings/invoices">Pay now</Link>
        </UILink>
      </div>
    </div>
  );
};
